import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Card.module.scss'
import Packshot from './Packshot/Packshot'
import TextOverlay from './TextOverlay'
import RatingOverlay from './RatingOverlay'
import ProgressBar from './ProgressBar'
import CloseButton from './CloseButton'
import { getLiveChannelUpdateURL, isChannelUpdated } from '../../../../utils/liveChannelRefresh'
import * as csm from '../../../../utils/csm'
import LiveStatus from './LiveStatus/LiveStatus'
import { FeatureSwitches, StorefrontStateItem } from '../../../../containers/Storefront';
import { Hover } from '../../../../../../DVWebNodeHover/Hover'
import { TrailerFragment, StorefrontItemPlaybackAction, ItemProgressBar, safeBool } from 'DVWebNodeUtils'

interface EditShieldProps {
  removeItem: Function
  triggerCarouselLazyload: Function
}

const EditShield = ({ removeItem, triggerCarouselLazyload }: EditShieldProps) => (
  <div className={cn(styles.EditShield, 'tst-remove-button')}>
    <CloseButton onClick={() => {
      removeItem()
      triggerCarouselLazyload()
    }} />
  </div>
)

const transformPlaybackAction = (storefrontAction?: StorefrontItemPlaybackAction, progress?: ItemProgressBar) => {
  if (storefrontAction) return {
    ...storefrontAction,
    titleID: storefrontAction.titleID || storefrontAction.channelId || '',
    progress: progress && progress.percentage,
    label: storefrontAction.label
  }
  return null
}

const DisabledShield = () => (
  <div className={styles.DisabledShield}></div>
)

interface CardProps extends StorefrontStateItem {
  updateChannelItem?: Function
  updateStateTimeout?: number
  isEditActive?: boolean
  isDisabled?: boolean
  isIsolatedCarousel?: boolean
  isRemoved?: boolean
  isTwinHero?: boolean
  hoverClass?: string
  isSquare?: boolean
  isPortrait?: boolean
  fetch?: Function
  featureSwitches?: FeatureSwitches
  canLivePlayback?: boolean
  showPlayButton?: boolean
  isVerticallyVisible?: boolean
  enrichSingleItem?: Function
  collectionType: string
  isLazy?: boolean
  removeItem?: Function
  triggerCarouselLazyload?: Function
  pos?: number
  trailer?: TrailerFragment
  showProgressBar?: boolean
  description?: string
  cardMetadata?: any // [N2M] This doesn't exist in storefront.xml model anymore. For TS compatibility and to minimise changes in migration, I'm declaring it as any and optional, to remove build issues
  isPhablet: boolean
  showPrerollsInHover?: boolean
  isHoverDisabled?: boolean
  takeFocusForCarousel?: () => void
  resetFocusForCarousel?: () => void
}

interface CardState {
  opacity: number
  hoverReady: boolean
  hoverIsExpanded: boolean
}

class Card extends React.PureComponent<CardProps, CardState> {
  static defaultProps = {
    updateStateTimeout: 500
  }

  constructor(props: CardProps) {
    super(props)
    this.state = {
      opacity: 1,
      hoverReady: false,
      hoverIsExpanded: false
    }
  }

  getAdditionalAttributes() {
    // To work, the hover requires
    // * the asin (or the gti) inside a data-asin attribute
    // * the lcid or channel GTI inside a data-lcid attribute
    // * the why recommended reasons, where applicable
    if (this.props.featureSwitches && this.props.featureSwitches.hover2019)
      return {}
    let attributes = {}
    if (this.props.hoverInfo && this.props.hoverInfo.canHover) {
      if (this.props.titleID) {
        attributes = { ...attributes, 'data-asin': this.props.titleID }
      }
      if (this.props.channelId) {
        attributes = { ...attributes, 'data-lcid': this.props.channelId }
      }
      if (this.props.hoverInfo && this.props.hoverInfo.whyRecommendedReasons) {
        attributes = { ...attributes, 'data-why-reasons': this.props.hoverInfo.whyRecommendedReasons }
      }
    }
    return attributes
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::Card')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a card')
  }

  onMouseEnterHandler() {
    this.props.takeFocusForCarousel && this.props.takeFocusForCarousel()
    this.onHoverExpand()
    if (this.props.featureSwitches && this.props.featureSwitches.hover2019)
      return
    const channelId = this.props.channelId

    if (channelId) {
      const apiUrl = getLiveChannelUpdateURL([channelId])

      this.props.fetch(apiUrl).then((data: any) => {
        const channelIdItem = data.outputMap[channelId]

        const hasChanged = isChannelUpdated(channelIdItem, this.props.image.url)

        if (hasChanged) {
          this.setState({ opacity: 0 })
          setTimeout(() => {
            this.props.updateChannelItem(channelId, {
              title: channelIdItem.title,
              titleID: channelIdItem.asin,
              image: { url: channelIdItem.imageURL }
            })
            this.setState({ opacity: 1 })
          }, this.props.updateStateTimeout)
        }
      })
    }
  }

  onHoverExpand() {
    if (!this.state.hoverReady) {
      this.setState({ hoverReady: true })
    }
    this.setState({ hoverIsExpanded: true })
  }

  onHoverRetract() {
    this.setState({ hoverIsExpanded: false })
  }

  render() {
    const canHover = !global.__NATIVEAPP__ && !this.props.isEditActive && !this.props.isDisabled &&
      safeBool(this.props.hoverInfo && this.props.hoverInfo.canHover)

    const isHover2019 = this.props.featureSwitches && this.props.featureSwitches.hover2019

    const isLive = Boolean(this.props.channelId)

    const pageWrapper = !this.props.isIsolatedCarousel
      ? styles.CardWrapper : styles.IsolatedCarouselCardWrapper
    const wrapperClasses = cn(pageWrapper, 'tst-card-wrapper', {
      [styles.isRemoved]: this.props.isRemoved,
      [styles.TwinHeroCardWrapper]: this.props.isTwinHero,
      ['tst-twin-hero-item']: this.props.isTwinHero,
      [styles.SquareCardWrapper]: this.props.isSquare,
      [styles.PortraitCardWrapper]: this.props.isPortrait,
      [this.props.hoverClass]: canHover && !isHover2019 //For old hover
    })
    const isPrime = this.props.entitlements && this.props.entitlements.indexOf('prime') >= 0
    const cardClasses = cn(
      styles.TitleCard,
      'tst-title-card',
      styles.Card,
      styles.darkcard,
      {
        [styles.isDisabled]: this.props.isDisabled,
        [styles.isRemoved]: this.props.isRemoved,
        [styles.overflowOnHover]: canHover && isHover2019
      }
    )

    const showFakePlayButton = this.props.cardMetadata &&
      safeBool(this.props.cardMetadata.showPlayButton) && !this.props.playbackAction

    const hasPrimeSashFeature = Boolean(this.props.featureSwitches &&
      this.props.featureSwitches.hasPrimeSash)
    const hasPackshotRatingFeature = Boolean(this.props.featureSwitches &&
      this.props.featureSwitches.packshotRating)
    const additionalAttributes = this.getAdditionalAttributes()

    const showLiveBadge = Boolean(this.props.liveInfo)
    const cardContents = <Packshot
      collectionType={this.props.collectionType}
      link={this.props.link}
      image={this.props.image}
      isTwinHero={this.props.isTwinHero}
      isPortrait={this.props.isPortrait}
      isLazy={this.props.isLazy}
      playbackAction={this.props.playbackAction}
      hasPrimeSash={isPrime && hasPrimeSashFeature}
      isLive={isLive}
      title={this.props.title}
      alternateText={this.props.alternateText}
      canLivePlayback={this.props.canLivePlayback}
      labelOnly={this.props.itemType === 'Label'}
      isVerticallyVisible={this.props.isVerticallyVisible}
      showFakePlayButton={showFakePlayButton}
      enrichSingleItem={this.props.enrichSingleItem}
      titleID={this.props.titleID}
      isIdPlayable={this.props.properties && this.props.properties.isIdPlayable}
      featureSwitches={this.props.featureSwitches}
      showPlayButton={this.props.showPlayButton}
      playButtonClassName={cn(styles.playButton, { [styles.isLive]: isLive })}
      isRounded={showLiveBadge}
      hoverIsExpanded={this.state.hoverIsExpanded}
    />
    const bentoHover = canHover &&
      isHover2019 && this.state.hoverReady
    const isChannel = this.props.channelId || (this.props.playbackAction && this.props.playbackAction.channelId)
    const maybeWrappedHoverContents = bentoHover ?
      <Hover
        className={styles.hover}
        placeholderClassName={styles.hoverPlaceholder}
        onMouseLeave={this.props.resetFocusForCarousel}
        offers={this.props.messages && this.props.messages.offers}
        infoboxes={this.props.messages && this.props.messages.infoboxes}
        // The Hover can infer the type, except if it's a channel
        titleIDType={isChannel ? "CHANNEL" : undefined}
        {...this.props}
        playbackAction={transformPlaybackAction(this.props.playbackAction, this.props.progress)}
        showPrerollsInHover={this.props.showPrerollsInHover}
        url={this.props.link && this.props.link.url}
        //This will keep the React component (so we don't lose Ajax-loaded state) but remove the DOM elements
        disabled={this.props.isHoverDisabled}
      >
        {cardContents}
      </Hover> : cardContents

    return (
      <li className={wrapperClasses}
        {...additionalAttributes}
        onMouseEnter={() => this.onMouseEnterHandler()}
        onMouseLeave={() => this.onHoverRetract()}
        onFocus={() => this.onHoverExpand()}
        onBlur={() => this.onHoverRetract()}
        style={{ opacity: this.state.opacity }}>
        <div className={cardClasses}>
          <div className={styles.PackshotContainer}>
            {maybeWrappedHoverContents}
            {this.props.showPlayButton && this.props.showProgressBar &&
              this.props.progress && <ProgressBar progress={this.props.progress} />}
          </div>
          {showLiveBadge &&
            <LiveStatus isStandalone={true} {...this.props.liveInfo} />}
          {hasPackshotRatingFeature && this.props.maturityRating &&
            (<RatingOverlay ratingObj={this.props.maturityRating} />)}
          {Boolean(this.props.isSquare) &&
            (<TextOverlay link={this.props.link} title={this.props.title} />)}
          {Boolean(this.props.link) && this.props.itemType === 'Label' && !Boolean(this.props.isSquare) &&
            (<TextOverlay
              link={this.props.link}
              title={this.props.link.label}
              description={this.props.title}
              centerText={true} />)}
          {Boolean(this.props.isEditActive && !this.props.isDisabled) &&
            <EditShield removeItem={this.props.removeItem}
              triggerCarouselLazyload={this.props.triggerCarouselLazyload} />
          }
          {Boolean(this.props.isDisabled) && <DisabledShield />}
        </div>
      </li>
    )
  }

}

export default Card
